import {createContext, FC, SetStateAction, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {ImageType} from 'react-images-uploading';
import {useNavigate} from 'react-router-dom';
import {WithChildren} from '../../../../../common/helpers';
import {routes} from '../../../../../routing/routes';
import {Store} from '../../../../auth/common/Types';
import useCampaignCreate from '../hooks/useCampaignCreate';
import {CampaignCreateData, Genre, Platform, Service, SocialPostType} from '../types/Types';
import {Gender} from '../../../../../common/types/Gender';
import {BusinessHour, Day} from '../../../../../common/types/BusinessTimes';

const totalStep = 5;

const steps = {
	basic: 1,
	address: 2,
	details: 3,
	influencer: 4,
	post: 5,
};

export type Step = keyof typeof steps;

interface CreateCampaignContextProps {
	currentStep: number;
	totalStep: number;
	isFirstStep: boolean;
	isLastStep: boolean;
	isCurrentlyOn: (step: Step) => boolean;
	goToNextStep: () => void;
	goToPrevStep: () => void;
	canGotoStep: (step: number) => boolean;
	gotoStep: (step: number) => void;
	canDraft: boolean;
	canNotGoNext: boolean;
	setCanNotGoNext: (value: boolean) => void;
	data: CampaignCreateData;
	saveAsDraft: (info: string) => void;
	setCurrentStep?: (step: number) => void;
}

const CreateCampaignContext = createContext<CreateCampaignContextProps>({
	currentStep: 1,
	totalStep,
	isFirstStep: false,
	isLastStep: false,
	isCurrentlyOn: (step: Step) => {
		console.log(step);
		return false;
	},
	goToNextStep: () => {},
	goToPrevStep: () => {},
	canGotoStep: (step: number) => {
		console.log(step);
		return false;
	},
	gotoStep: (step: number) => {
		console.log(step);
	},
	canDraft: false,
	canNotGoNext: false,
	setCanNotGoNext: (value: boolean) => {
		console.log(value);
	},
	data: {
		setGenre: (genre: Genre) => {
			console.log(genre);
		},
		setService: (service: Service) => {
			console.log(service);
		},
		socialPostTypes: [],
		setSocialPostTypes: (types: SetStateAction<SocialPostType[]>) => {
			console.log(types);
		},
		setStore: (store: Store) => {
			console.log(store);
		},
		setTitle: (title: string) => {
			console.log(title);
		},
		setThumb: (thumb: ImageType) => {
			console.log(thumb);
		},
		images: [],
		setImages: (images: ImageType[]) => {
			console.log(images);
		},
		setSummary: (summary: string) => {
			console.log(summary);
		},
		setCharge: (charge: number) => {
			console.log(charge);
		},
		holidays: [],
		setHolidays: (days: SetStateAction<Day[]>) => {
			console.log(days);
		},
		setHours: (hours: BusinessHour) => {
			console.log(hours);
		},
		setSlots: (slots: number) => {
			console.log(slots);
		},
		setTreatmentTime: (slots: number) => {
			console.log(slots);
		},
		genders: [],
		setGenders: (genders: SetStateAction<Gender[]>) => {
			console.log(genders);
		},
		updateFollowers: (platform: Platform, val: number | undefined) => {
			console.log(platform, val);
		},
		hashtags: [],
		setHashtags: (tags: SetStateAction<string[]>) => {
			console.log(tags);
		},
		setCompanions: (companions: number) => {
			console.log(companions);
		},
		totalSlots: 0,
		increaseModalOpen: false,
		setIncreaseModalOpen: (open: boolean) => {
			console.log(open);
		},
		hasCompanion: true,
		setHasCompanion: (companion: boolean) => {
			console.log(companion);
		},
		refetch: () => {
			console.log('slots');
		},
	},
	saveAsDraft: () => {},
});

const CreateCampaignProvider: FC<WithChildren> = ({children}) => {
	const [currentStep, setCurrentStep] = useState(1);
	const [maxStep, setMaxStep] = useState(1);
	const {id, data, saveAsDraft} = useCampaignCreate();
	const [canNotGoNext, setCanNotGoNext] = useState(false);
	const navigate = useNavigate();

	const isFirstStep = currentStep === 1;

	const isLastStep = currentStep === totalStep;

	if (isLastStep) {
		localStorage.removeItem('currentStep');
	}

	const isCurrentlyOn = useCallback(
		(key: Step) => {
			return steps[key] === currentStep;
		},
		[currentStep],
	);

	const saveAsDraftAndNavigate = useCallback(async () => {
		await saveAsDraft('confirm');
		if (id) {
			navigate(routes.campaign.info.replace(':id', `details/${id}`));
		}
	}, [saveAsDraft, navigate, id]);

	const goToNextStep = useCallback(async () => {
		if (isLastStep) {
			saveAsDraftAndNavigate();
			return;
		}
		setCurrentStep((current) => {
			const next = current < totalStep ? current + 1 : current;
			setMaxStep(next);
			return next;
		});
	}, [isLastStep, setCurrentStep, saveAsDraftAndNavigate]);

	useEffect(() => {
		setMaxStep((currentMax) => (currentMax < currentStep ? currentStep : currentMax));
	}, [currentStep, navigate]);

	const goToPrevStep = useCallback(() => {
		setCurrentStep((current) => (current > 1 ? current - 1 : current));
	}, [setCurrentStep]);

	const canGotoStep = useCallback((step: number) => step <= maxStep, [maxStep]);

	const gotoStep = useCallback(
		(step: number) => {
			if (!canGotoStep(step)) return;

			setCurrentStep(step);
		},
		[canGotoStep, setCurrentStep],
	);

	const canDraft = useMemo(() => currentStep >= steps.details, [currentStep]);

	const value = useMemo(() => {
		return {
			currentStep,
			totalStep,
			isFirstStep,
			isLastStep,
			isCurrentlyOn,
			goToNextStep,
			goToPrevStep,
			canGotoStep,
			gotoStep,
			canDraft,
			canNotGoNext,
			setCanNotGoNext,
			data,
			saveAsDraft,
			setCurrentStep,
		};
	}, [
		currentStep,
		isFirstStep,
		isLastStep,
		isCurrentlyOn,
		goToNextStep,
		goToPrevStep,
		canGotoStep,
		gotoStep,
		canDraft,
		canNotGoNext,
		data,
		saveAsDraft,
		setCurrentStep,
	]);

	return <CreateCampaignContext.Provider value={value}>{children}</CreateCampaignContext.Provider>;
};

export function useCreateCampaignJourney() {
	return useContext(CreateCampaignContext);
}

export default CreateCampaignProvider;
